import { Tree } from '@/shared/lib/utils/tree/types';
import _ from 'lodash';
// Define the overload signatures
export type TreeMapFunction = {
  <
    T extends Record<string, any>,
    R extends { [Property in keyof T]?: T[Property] | any } & Record<
      string,
      any
    >,
    K extends (keyof T & string) | string = 'children',
  >(
    arr: T[],
    childrenKey: K,
    transformer: (
      val: T,
      options?: {
        depth?: number;
        parent?: T;
        index?: number;
        path?: T[];
        indexPath?: number[];
      },
    ) => R,
  ): Tree<R, K>;

  <
    T extends Record<string, any>,
    R extends { [Property in keyof T]?: T[Property] | any } & Record<
      string,
      any
    >,
    K extends (keyof T & string) | string = 'children',
  >(
    arr: T[],
    childrenKey: K,
    childKeyOverride: K | string,
    transformer: (
      val: T,
      options?: {
        depth?: number;
        parent?: T;
        index?: number;
        path?: T[];
        indexPath?: number[];
      },
    ) => R,
  ): Tree<R, K>;
};

export const treeMap: TreeMapFunction = <
  T extends Record<string, any>,
  R extends { [Property in keyof T]?: T[Property] | any } & Record<string, any>,
  K extends (keyof T & string) | string = 'children',
>(
  arr: T[],
  childrenKey: K,
  childKeyOverrideOrTransformer:
    | (K | string)
    | ((
        val: T,
        options?: {
          depth?: number;
          parent?: T;
          index?: number;
          path?: T[];
          indexPath?: number[];
        },
      ) => R),
  transformer?: (
    val: T,
    options?: {
      depth?: number;
      parent?: T;
      index?: number;
      path?: T[];
      indexPath?: number[];
    },
  ) => R,
): Tree<R, K> => {
  const childKeyOverride =
    typeof childKeyOverrideOrTransformer === 'function'
      ? childrenKey
      : (childKeyOverrideOrTransformer as K);
  const actualTransformer =
    typeof childKeyOverrideOrTransformer === 'function'
      ? childKeyOverrideOrTransformer
      : transformer!;

  const inner = (
    arr: T[] = [],
    depth: number = 0,
    parent?: T,
    path: T[] = [],
    indexPath: number[] = [],
  ) => {
    return arr.map((item, i) => {
      const { [childrenKey]: children } = item;
      const currentNode: T = item;

      const transformedItem = actualTransformer(item, {
        depth,
        parent,
        index: i,
        path: [...path, currentNode],
        indexPath: [...indexPath, i],
      });

      return {
        ...(childKeyOverride !== childrenKey
          ? _.omit(transformedItem, childrenKey)
          : transformedItem),
        ...(children && {
          [childKeyOverride]: inner(
            (transformedItem[childKeyOverride] as T[]) || (children as T[]),
            depth + 1,
            item,
            [...path, currentNode],
            [...indexPath, i],
          ),
        }),
      };
    });
  };

  return inner(arr);
};